import React, { useState } from 'react';

import BookmarkIcon from '@mui/icons-material/BookmarkOutlined';
import HelpIcon from '@mui/icons-material/HelpOutlineOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import {
  Navigate, useParams,
} from 'react-router';

import Breadcrumbs from '../../components/Breadcrumbs';
import PageContent from '../../components/PageContent';
import { LoadingWrapper } from '../../components/PageLayout';
import SkeletonStack from '../../components/SkeletonStack';
import Tabs from '../../components/Tabs';
import {
  useGetExamQuery,
} from '../../services/exams';
import { SUBJECTS } from '../../utils/common/constants';
import useTab from '../../utils/react/hooks/useTab';

import DetailsTab from './DetailsTab';
import {
  BreadcrumbsContainer, ContentContainer, PageContainer,
} from './index.styles';
import QuestionDetails from './QuestionDetails';
import QuestionsTab from './QuestionsTab';
import SettingsTab from './SettingsTab';

enum Tab {
  Details = 'details',
  Questions = 'questions',
  Settings = 'settings',
}

const TAB_OPTIONS = [
  {
    key: Tab.Details,
    label: 'Details',
    icon: <BookmarkIcon />,
  },
  {
    key: Tab.Questions,
    label: 'Questions',
    icon: <HelpIcon />,
  },
  {
    key: Tab.Settings,
    label: 'Settings',
    icon: <SettingsIcon />,
  },
];

const AdminExamPage = () => {
  const { subject, exam: examId } = useParams();

  if (!subject || !examId) {
    return (
      <Navigate to="/admin/exams" replace />
    );
  }

  const [tab, setTab] = useTab({ default: Tab.Details });
  const [questionId, setQuestionId] = useState<string | undefined>(undefined);

  const handleTabChange = (key: string) => {
    setTab(key);
    setQuestionId(undefined);
  };

  const { data: exam, isLoading } = useGetExamQuery(
    { subject, examId }, { skip: !subject || !examId },
  );

  if (!exam?.data) {
    return <SkeletonStack />;
  }

  return (
    <PageContent
      sideBarOpen={!!questionId}
      sideBarWidth={600}
      sideBar={(
        <QuestionDetails
          subject={subject}
          exam={exam.data}
          examId={examId}
          questionId={questionId}
          onClose={() => setQuestionId(undefined)}
        />
      )}
    >
      <PageContainer>
        <LoadingWrapper loading={isLoading}>
          <ContentContainer direction="column" spacing={2}>
            <BreadcrumbsContainer>
              <Breadcrumbs
                title={exam?.data.label}
                path={[
                  {
                    label: SUBJECTS[exam?.data.subject]?.label || 'Exams',
                    href: `/admin/exams/${exam?.data.subject}`,
                  },
                ]}
              />
            </BreadcrumbsContainer>
            <Tabs options={TAB_OPTIONS} selectedKey={tab} onChange={handleTabChange} />
            {
              tab === Tab.Details && <DetailsTab exam={exam.data} examMetadata={exam.metadata} />
            }
            {
              tab === Tab.Questions && (
                <QuestionsTab
                  exam={exam.data}
                  selectedQuestionId={questionId}
                  onQuestionClick={(id) => setQuestionId(id)}
                />
              )
            }
            {
              tab === Tab.Settings && <SettingsTab exam={exam.data} />
            }
          </ContentContainer>
        </LoadingWrapper>
      </PageContainer>
    </PageContent>
  );
};

export default AdminExamPage;
